import { lazy } from "react";

import { IRoute, IRouteType } from "App/types";
import {
  AcceptInvitationPage,
  AdminBand,
  AdminDashboard,
  AnswerRSVPPage,
  BandAssetsPage,
  BandSettingsPage,
  CalendarPage,
  CompanyDashboard,
  CompanyInfo,
  CompanyPaymentError,
  CompanyPaymentSuccess,
  ContactsOverviewPage,
  CreateEventInfoBlockPage,
  CreateEventPage,
  CreateEventVenueBlockPage,
  CreateGuestlistBlockPage,
  CreateInvitationPage,
  CreatePeopleBlockPage,
  CreateScheduleBlockPage,
  CreateTravelBlockPage,
  EditCompanyInfo,
  EditContactDetailsPage,
  EditDocumentsPage,
  EditEventInfoBlockPage,
  EditEventPage,
  EditEventSettingsPage,
  EditEventVenueBlockPage,
  EditGuestlistBlockPage,
  EditInvitationPage,
  EditMemberPage,
  EditMemberSettingsPage,
  EditPeopleBlockPage,
  EditPermissionsPage,
  EditPreferencesPage,
  EditProfilePage,
  EditRegionalSettingsPage,
  EditScheduleBlockPage,
  EditSubscriptionPlan,
  EditTravelBlockPage,
  EventPage,
  Homepage,
  MemberOverviewPage,
  MyVenues,
  PermissionGroupPage,
  PermissionsOverviewPage,
  PlacesOverviewPage,
  ProfilePage,
  SubscriptionBillingInfo,
  SubscriptionError,
  SubscriptionOverview,
  SubscriptionPending,
  SubscriptionSuccess,
  ViewFullProfilePage,
} from "Bands/pages";

import { CreateAccommodationBlockPage } from "Bands/pages/event-accommodation-block/CreateAccommodationBlock";
import { EditAccommodationBlockPage } from "Bands/pages/event-accommodation-block/EditAccommodationBlock";
import { VENUE_PATHS } from "Venues/router";
import { IPermissionKey } from "shared/types";
import { BAND_PATHS } from "./constants";

const ProfilePageRouter = lazy(() => import("./ProfilePageRouter"));
const BandSettingsRouter = lazy(() => import("./BandSettingsRouter"));
const SubscriptionRouter = lazy(() => import("./SubscriptionRouter"));
const EventRouter = lazy(() => import("./EventRouter"));
const CompanyRouter = lazy(() => import("./CompanyRouter"));
const AdminRouter = lazy(() => import("./AdminRouter"));
const MembersRouter = lazy(() => import("./MembersRouter"));
const BandAssetsRouter = lazy(() => import("./BandAssetsRouter"));
const VenuePageRouter = lazy(() => import("Venues/router/VenuePageRouter"));
const ExportRouter = lazy(() => import("./ExportRouter"));

export const bandRoutes: IRoute[] = [
  {
    Component: Homepage,
    path: BAND_PATHS.HOME,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: CalendarPage,
    path: BAND_PATHS.CALENDAR,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: CalendarPage,
    path: BAND_PATHS.BAND_CALENDAR,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: ProfilePageRouter,
    path: BAND_PATHS.PROFILE,
  },
  {
    Component: BandSettingsRouter,
    path: BAND_PATHS.BAND_SETTINGS,
  },
  {
    Component: SubscriptionRouter,
    path: BAND_PATHS.SUBSCRIPTION,
  },
  {
    Component: CreateEventPage,
    path: BAND_PATHS.CREATE_BAND_EVENT,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.createEvent,
  },
  {
    Component: EventRouter,
    path: BAND_PATHS.BAND_EVENT,
  },
  {
    Component: CompanyRouter,
    path: BAND_PATHS.COMPANY,
  },
  {
    Component: AdminRouter,
    path: BAND_PATHS.ADMIN,
    type: IRouteType.admin,
  },
  {
    Component: MembersRouter,
    path: BAND_PATHS.MEMBERS,
    type: IRouteType.private,
  },
  {
    Component: BandAssetsRouter,
    path: BAND_PATHS.BAND_ASSETS,
    type: IRouteType.private,
  },
  {
    Component: AcceptInvitationPage,
    path: BAND_PATHS.ACCEPT_INVITATION,
    exact: true,
    type: IRouteType.public,
  },
  {
    Component: AnswerRSVPPage,
    path: BAND_PATHS.ANSWER_RSVP,
    exact: true,
  },
  {
    Component: VenuePageRouter,
    path: VENUE_PATHS.VENUE_PAGE_ROUTER,
  },
  {
    Component: MyVenues,
    path: BAND_PATHS.MY_VENUES,
    type: IRouteType.private,
  },
  {
    Component: ExportRouter,
    path: BAND_PATHS._EVENT_DETAILS_EXPORT,
    exact: true,
  },
];

export const companyRoutes: IRoute[] = [
  {
    Component: CompanyDashboard,
    path: BAND_PATHS.COMPANY_DASHBOARD,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: CompanyInfo,
    path: BAND_PATHS.COMPANY_INFO,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: EditCompanyInfo,
    path: BAND_PATHS.EDIT_COMPANY_INFO,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.edit,
  },
  {
    Component: CompanyPaymentSuccess,
    path: BAND_PATHS.COMPANY_PAYMENT_SUCCESS,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: CompanyPaymentError,
    path: BAND_PATHS.COMPANY_PAYMENT_ERROR,
    exact: true,
    type: IRouteType.private,
  },
];

export const subscriptionRoutes: IRoute[] = [
  {
    Component: SubscriptionOverview,
    path: BAND_PATHS.SUBSCRIPTION_OVERVIEW,
    exact: true,
    // not a permission route because permission is handled in SubscriptionRouter.tsx
    type: IRouteType.private,
  },
  {
    Component: SubscriptionBillingInfo,
    path: BAND_PATHS.SUBSCRIPTION_BILLING_INFO,
    exact: true,
    // not a permission route because permission is handled in SubscriptionRouter.tsx
    type: IRouteType.private,
  },
  {
    Component: SubscriptionSuccess,
    path: BAND_PATHS.SUBSCRIPTION_SUCCESS,
    exact: true,
    // not a permission route because permission is handled in SubscriptionRouter.tsx
    type: IRouteType.private,
  },
  {
    Component: SubscriptionError,
    path: BAND_PATHS.SUBSCRIPTION_ERROR,
    exact: true,
    // not a permission route because permission is handled in SubscriptionRouter.tsx
    type: IRouteType.private,
  },
  {
    Component: SubscriptionPending,
    path: BAND_PATHS.SUBSCRIPTION_PENDING,
    exact: true,
    // not a permission route because permission is handled in SubscriptionRouter.tsx
    type: IRouteType.private,
  },
  {
    Component: EditSubscriptionPlan,
    path: BAND_PATHS.EDIT_SUBSCRIPTION_PLAN,
    exact: true,
    // not a permission route because permission is handled in SubscriptionRouter.tsx
    type: IRouteType.private,
  },
];

export const profileRoutes: IRoute[] = [
  {
    Component: EditContactDetailsPage,
    path: BAND_PATHS.EDIT_PROFILE_CONTACT,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: EditDocumentsPage,
    path: BAND_PATHS.EDIT_PROFILE_DOCUMENTS,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: EditPreferencesPage,
    path: BAND_PATHS.EDIT_PROFILE_PREFERENCES,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: EditProfilePage,
    path: BAND_PATHS.EDIT_PROFILE,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: ProfilePage,
    path: BAND_PATHS.PROFILE,
    exact: true,
    type: IRouteType.private,
  },
];

export const bandSettingsRoutes: IRoute[] = [
  {
    Component: EditRegionalSettingsPage,
    path: BAND_PATHS.EDIT_BAND_SETTINGS_REGIONAL,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.edit,
  },
  {
    Component: EditEventSettingsPage,
    path: BAND_PATHS.EDIT_BAND_SETTINGS_EVENT,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.edit,
  },
  {
    Component: EditMemberSettingsPage,
    path: BAND_PATHS.EDIT_BAND_SETTINGS_MEMBER,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.edit,
  },
  {
    Component: BandSettingsPage,
    path: BAND_PATHS.BAND_SETTINGS,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.delete,
  },
];

export const memberRoutes: IRoute[] = [
  {
    Component: MemberOverviewPage,
    path: BAND_PATHS.MEMBERS,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: CreateInvitationPage,
    path: BAND_PATHS.CREATE_MEMBER,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.addUser,
  },
  {
    Component: EditInvitationPage,
    path: BAND_PATHS.EDIT_INVITATION,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.addUser,
  },
  {
    Component: EditMemberPage,
    path: BAND_PATHS.EDIT_MEMBER,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.addUser,
  },
  {
    Component: ViewFullProfilePage,
    path: BAND_PATHS.VIEW_MEMBER,
    exact: true,
    // not a permission route because permission is not returned in band
    type: IRouteType.private,
  },
  {
    Component: PermissionsOverviewPage,
    path: BAND_PATHS.PERMISSIONS,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.addUser,
  },
  {
    Component: PermissionGroupPage,
    path: BAND_PATHS.PERMISSION_GROUP,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.addUser,
  },
  {
    Component: EditPermissionsPage,
    path: BAND_PATHS.EDIT_PERMISSION,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.addUser,
  },
];

export const bandAssetsRoutes: IRoute[] = [
  {
    Component: BandAssetsPage,
    path: BAND_PATHS.BAND_ASSETS,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: ContactsOverviewPage,
    path: BAND_PATHS.BAND_ASSETS_CONTACTS,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: PlacesOverviewPage,
    path: BAND_PATHS.BAND_ASSETS_PLACES,
    exact: true,
    type: IRouteType.private,
  },
];

export const eventRoutes: IRoute[] = [
  {
    Component: EventPage,
    path: BAND_PATHS.BAND_EVENT,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: EditEventPage,
    path: BAND_PATHS.EDIT_BAND_EVENT,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.edit,
  },
  {
    Component: CreatePeopleBlockPage,
    path: BAND_PATHS.CREATE_PEOPLE_BLOCK,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.createPeopleBlock,
  },
  {
    Component: EditPeopleBlockPage,
    path: BAND_PATHS.EDIT_PEOPLE_BLOCK,
    exact: true,
    // not a permission route because permission is not returned in event
    type: IRouteType.private,
  },
  {
    Component: CreateScheduleBlockPage,
    path: BAND_PATHS.CREATE_SCHEDULE_BLOCK,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.createScheduleBlock,
  },
  {
    Component: EditScheduleBlockPage,
    path: BAND_PATHS.EDIT_SCHEDULE_BLOCK,
    exact: true,
    // not a permission route because permission is not returned in event
    type: IRouteType.private,
  },
  {
    Component: CreateEventInfoBlockPage,
    path: BAND_PATHS.CREATE_INFO_BLOCK,
    exact: true,
    // not a permission route because permission is not returned in event
    type: IRouteType.private,
  },
  {
    Component: EditEventInfoBlockPage,
    path: BAND_PATHS.EDIT_INFO_BLOCK,
    exact: true,
    // not a permission route because permission is not returned in event
    type: IRouteType.private,
  },
  {
    Component: CreateEventVenueBlockPage,
    path: BAND_PATHS.CREATE_VENUE_BLOCK,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.createVenueBlock,
  },
  {
    Component: EditEventVenueBlockPage,
    path: BAND_PATHS.EDIT_VENUE_BLOCK,
    exact: true,
    type: IRouteType.private,
  },
  {
    Component: CreateGuestlistBlockPage,
    path: BAND_PATHS.CREATE_GUESTLIST_BLOCK,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.createGuestlistBlock,
  },
  {
    Component: EditGuestlistBlockPage,
    path: BAND_PATHS.EDIT_GUESTLIST_BLOCK,
    exact: true,
    // not a permission route because permission is not returned in event
    type: IRouteType.private,
  },
  {
    Component: CreateTravelBlockPage,
    path: BAND_PATHS.CREATE_TRAVEL_BLOCK,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.createTravelBlock,
  },
  {
    Component: EditTravelBlockPage,
    path: BAND_PATHS.EDIT_TRAVEL_BLOCK,
    exact: true,
    // not a permission route because permission is not returned in event
    type: IRouteType.private,
  },
  {
    Component: CreateAccommodationBlockPage,
    path: BAND_PATHS.CREATE_ACCOMMODATION_BLOCK,
    exact: true,
    type: IRouteType.permission,
    permissionType: IPermissionKey.createAccommodationBlock,
  },
  {
    Component: EditAccommodationBlockPage,
    path: BAND_PATHS.EDIT_ACCOMMODATION_BLOCK,
    exact: true,
    // not a permission route because permission is not returned in event
    type: IRouteType.private,
  },
];

export const adminRoutes: IRoute[] = [
  {
    Component: AdminDashboard,
    path: BAND_PATHS.ADMIN_DASHBOARD,
    exact: true,
  },
  {
    Component: AdminBand,
    path: BAND_PATHS.ADMIN_BAND,
    exact: true,
  },
];
